import { useCallback, useEffect, useRef, useState } from 'react';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => void;
  onImageUpload?: (file: File) => void;
  imagePreviewUrl?: string | null;
  onClearSearch?: () => void;
  placeholder?: string;
  isLoading?: boolean;
  isImageLoading?: boolean;
}

export default function SearchBar({
  value,
  onChange,
  onSearch,
  onImageUpload,
  imagePreviewUrl = null,
  onClearSearch,
  placeholder = 'Try a name, 204/XY-P, or any marketplace ID',
  isLoading = false,
  isImageLoading = false,
}: SearchBarProps) {
  const [isImagePanelOpen, setIsImagePanelOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const hasImage = Boolean(imagePreviewUrl);

  const closeImagePanel = useCallback(() => {
    setIsImagePanelOpen(false);
    setIsDragging(false);
  }, []);

  const handleClear = useCallback(() => {
    if (onClearSearch) {
      onClearSearch();
    } else {
      onChange('');
    }
    inputRef.current?.focus();
  }, [onChange, onClearSearch]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const query = value.trim();
    if (!hasImage && query && !isLoading) {
      onSearch(query);
    }
  };

  const handleImageUpload = useCallback((file: File) => {
    inputRef.current?.blur();
    onImageUpload?.(file);
    closeImagePanel();
  }, [closeImagePanel, onImageUpload]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleImageUpload(file);
    event.target.value = '';
  };

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file?.type.startsWith('image/')) {
      handleImageUpload(file);
    }
  }, [handleImageUpload]);

  useEffect(() => {
    if (!isImagePanelOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeImagePanel();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeImagePanel();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeImagePanel, isImagePanelOpen]);

  useEffect(() => {
    if (!onImageUpload) return;

    const handleDragEnter = (event: DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      dragCounterRef.current += 1;
      setIsImagePanelOpen(true);
      setIsDragging(true);
    };
    const handleDragLeave = (event: DragEvent) => {
      event.preventDefault();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) setIsDragging(false);
    };
    const handleDragOver = (event: DragEvent) => event.preventDefault();
    const handleDocumentDrop = (event: DragEvent) => {
      event.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDocumentDrop);
    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDocumentDrop);
    };
  }, [onImageUpload]);

  useEffect(() => {
    if (!onImageUpload) return;
    const handlePaste = (event: ClipboardEvent) => {
      const imageItem = Array.from(event.clipboardData?.items || []).find((item) => item.type.startsWith('image/'));
      const imageFile = imageItem?.getAsFile();
      if (!imageFile) return;
      event.preventDefault();
      handleImageUpload(imageFile);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleImageUpload, onImageUpload]);

  return (
    <div ref={containerRef} className="w-full">
      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />

      <div className="overflow-hidden rounded-[24px] border border-white/10 bg-white shadow-panel">
        <form onSubmit={handleSubmit} className="p-3 sm:p-4">
          <label htmlFor="global-card-search" className="eyebrow mb-2.5 block px-1 text-secondary-light">
            Search by name, printed number, or marketplace ID
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative min-w-0 flex-1">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-secondary-light" aria-hidden="true">
                {hasImage ? (
                  <img src={imagePreviewUrl || undefined} alt="" className="h-9 w-9 rounded-lg border border-border object-cover" />
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m2.35-5.65a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z" />
                  </svg>
                )}
              </span>
              <input
                ref={inputRef}
                id="global-card-search"
                type="search"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={hasImage ? 'Image selected for recognition' : placeholder}
                disabled={isLoading || hasImage}
                className={`h-14 w-full rounded-2xl border border-border bg-background-secondary text-[15px] text-primary placeholder:text-secondary-light focus:border-cyan focus:bg-white focus:outline-none ${hasImage ? 'pl-16' : 'pl-12'} pr-12`}
              />
              {(hasImage || value.trim()) && (
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={isImageLoading}
                  className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-secondary-light transition-colors hover:bg-white hover:text-primary disabled:opacity-40"
                  aria-label={hasImage ? 'Clear selected image' : 'Clear search'}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {onImageUpload && (
              <button
                type="button"
                onClick={() => setIsImagePanelOpen((open) => !open)}
                disabled={isImageLoading}
                className="inline-flex h-14 items-center justify-center gap-2 rounded-2xl border border-border bg-white px-5 text-sm font-semibold text-primary transition-colors hover:border-cyan/50 hover:bg-cyan/5 disabled:cursor-wait disabled:opacity-50"
                aria-expanded={isImagePanelOpen}
              >
                <svg className="h-5 w-5 text-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8.8A1.8 1.8 0 0 1 4.8 7h1.13a2 2 0 0 0 1.66-.89l.82-1.22A2 2 0 0 1 10.07 4h3.86a2 2 0 0 1 1.66.89l.82 1.22a2 2 0 0 0 1.66.89h1.13A1.8 1.8 0 0 1 21 8.8v9.4a1.8 1.8 0 0 1-1.8 1.8H4.8A1.8 1.8 0 0 1 3 18.2V8.8Z" />
                  <circle cx="12" cy="13" r="3.2" strokeWidth={1.8} />
                </svg>
                {isImageLoading ? 'Recognizing…' : 'Search by image'}
              </button>
            )}

            <button
              type="submit"
              disabled={isLoading || !value.trim() || hasImage}
              className="inline-flex h-14 items-center justify-center gap-2 rounded-2xl bg-ink px-6 text-sm font-bold text-white transition-all hover:bg-slate disabled:cursor-not-allowed disabled:opacity-35"
            >
              {isLoading ? 'Resolving…' : 'Find card'}
              {!isLoading && (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m9 18 6-6-6-6" />
                </svg>
              )}
            </button>
          </div>
        </form>

        {isImagePanelOpen && onImageUpload && (
          <div className="border-t border-border bg-background-secondary p-3 sm:p-4">
            <div
              onDrop={handleDrop}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDragging(false);
              }}
              className={`rounded-[20px] border-2 border-dashed px-5 py-7 text-center transition-colors ${isDragging ? 'border-cyan bg-cyan/10' : 'border-border bg-white'}`}
            >
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-ink text-cyan">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
                </svg>
              </div>
              <h3 className="mt-3 text-sm font-bold text-primary">Drop a Pokémon card image here</h3>
              <p className="mt-1 text-xs leading-5 text-secondary">A full card, crop, screenshot, pasted image, or camera photo works best when the printed artwork is clear.</p>
              <div className="mt-5 flex flex-col justify-center gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isImageLoading}
                  className="rounded-xl bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate disabled:opacity-50"
                >
                  Choose image
                </button>
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={isImageLoading}
                  className="rounded-xl border border-border bg-white px-5 py-2.5 text-sm font-semibold text-primary hover:border-cyan/50 sm:hidden"
                >
                  Take a photo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
